/**
 * Webhook-health API client — #1438.
 *
 * Read-only per-topic delivery-health snapshot from the rebuilt backend
 * (`backend/src/platform/webhook/health-routes.ts`):
 *
 *   GET /api/webhook-events/health?windowHours=N   per-topic health snapshot
 *
 * Reads are gated by `webhook_failures:read` server-side (reused, not a new
 * grant). `http.get` returns the UNWRAPPED inner `data` (ADR-0006 §5), so the
 * caller reads `.providers` / `.window` directly.
 *
 * The snapshot shape comes from the generated OpenAPI types (ADR-0006) — a
 * backend contract change fails compilation here rather than at render time.
 * The backend returns ONLY raw per-topic metrics (+ `expected`); the health
 * BADGE is a frontend display derivation (see `WebhookHealthPage`).
 */

import { http } from '@/lib/api';
import type { components } from '@/types/api.generated';

export type WebhookHealthSnapshot = components['schemas']['WebhookHealthSnapshot'];
export type WebhookProviderHealth = WebhookHealthSnapshot['providers'][number];
export type WebhookTopicHealth = WebhookProviderHealth['topics'][number];

/**
 * The closed set of health windows the backend accepts (hours). An
 * out-of-set value 400s server-side, so the UI only ever offers these.
 */
export const WEBHOOK_HEALTH_WINDOWS = [6, 24, 72, 168, 720] as const;
export type WebhookHealthWindow = (typeof WEBHOOK_HEALTH_WINDOWS)[number];

export const DEFAULT_WEBHOOK_HEALTH_WINDOW: WebhookHealthWindow = 24;

/** Narrow an arbitrary number to a supported window, or null if unsupported. */
export function asWebhookHealthWindow(value: number): WebhookHealthWindow | null {
  return (WEBHOOK_HEALTH_WINDOWS as readonly number[]).includes(value)
    ? (value as WebhookHealthWindow)
    : null;
}

export const webhookHealthAPI = {
  async get(windowHours: number): Promise<WebhookHealthSnapshot> {
    const qs = new URLSearchParams({ windowHours: String(windowHours) });
    return http.get<WebhookHealthSnapshot>(`/api/webhook-events/health?${qs.toString()}`);
  },
};
